import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  TextField,
  Grid,
  ClickAwayListener,
  InputAdornment,
  FormControl,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  KeyboardArrowDown,
  Search,
  Check,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../utils/api';
import { toast } from 'react-toastify';
import { checkSupplierProfileComplete } from '../../utils/profileCheck';
import { isValidKenyaCompanyReg, KENYA_REG_HELPER, isValidEmail, EMAIL_HELPER } from '../../utils/kenyaValidators';
import Footer from '../../components/Footer/Footer';
import { countries } from '../../utils/countries';

const entityTypes = [
  'Private/Public Company',
  'Partnerships',
  'Foreign Company',
  'Individual/Sole Proprietor',
  'Trust',
];

const mapEntityTypeToDisplay = (value) => {
  const mapping = {
    private_company: 'Private/Public Company',
    public_company: 'Private/Public Company',
    partnership: 'Partnerships',
    foreign_company: 'Foreign Company',
    individual: 'Individual/Sole Proprietor',
    trust: 'Trust',
    other: 'Private/Public Company',
    'Public/Private Company': 'Private/Public Company',
    'Limited Company': 'Private/Public Company',
    'Public Limited Company': 'Private/Public Company',
    'Partnership': 'Partnerships',
    'Sole Proprietorship': 'Individual/Sole Proprietor',
  };
  return mapping[value] || value || '';
};

const RESTRICTED_ROLES = ['procurement', 'legal', 'management'];

// Entity type decides which documents the application carries, so the supplier
// may change it only while the application is a draft or returned to them.
// The server enforces the same rule.
const ENTITY_TYPE_EDITABLE = ['draft', 'more_info_required'];

const EditCompanyDetails = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  // The entity type as loaded, to send it only when the supplier changes it:
  // the list below shows several stored types under one name, so sending it
  // back unchanged would quietly rewrite, say, a public company as private.
  const [loadedEntityType, setLoadedEntityType] = useState('');

  // Form state
  const [formData, setFormData] = useState({
    supplierName: '',
    entityType: '',
    registeredCountry: '',
    companyRegistrationNumber: '',
    companyEmail: '',
    companyWebsite: '',
    physicalAddress: '',
    comment: '',
  });

  // Country selector state
  const [countrySearchOpen, setCountrySearchOpen] = useState(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState('');
  const [countryAnchorEl, setCountryAnchorEl] = useState(null);

  useEffect(() => {
    if (RESTRICTED_ROLES.includes(user?.role)) {
      navigate('/profile', { replace: true });
      return;
    }
    fetchSupplierData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchSupplierData = async () => {
    if (user?.role === 'supplier') {
      try {
        const response = await api.get('/suppliers');
        const suppliers = response.data.data || [];
        if (suppliers.length > 0) {
          const supplierData = suppliers[0];
          setSupplier(supplierData);
          setLoadedEntityType(mapEntityTypeToDisplay(supplierData.entityType) || '');

          const address = supplierData.companyPhysicalAddress;
          const fullAddress = address
            ? `${address.street || ''}, ${address.city || ''}, ${address.country || ''}${address.postalCode ? `, ${address.postalCode}` : ''}`.replace(/^,\s*|,\s*$/g, '')
            : supplierData.physicalAddress || '';

          setFormData({
            supplierName: supplierData.supplierName || '',
            entityType: mapEntityTypeToDisplay(supplierData.entityType) || '',
            registeredCountry: supplierData.registeredCountry || address?.country || '',
            companyRegistrationNumber: supplierData.companyRegistrationNumber || '',
            companyEmail: supplierData.companyEmail || '',
            companyWebsite: supplierData.companyWebsite || '',
            physicalAddress: fullAddress,
            comment: '',
          });
        }
      } catch (error) {
        console.error('Error fetching supplier data:', error);
      }
    }
    setLoading(false);
  };

  const entityTypeLocked = Boolean(supplier) && !ENTITY_TYPE_EDITABLE.includes(supplier.status);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Matches the code as well as the name, as the application form does.
  const filteredCountries = countries.filter((country) => {
    const term = countrySearchTerm.trim().toLowerCase();
    if (!term) return true;
    return country.name.toLowerCase().includes(term) || country.code.toLowerCase() === term;
  });

  // Fields the portal requires before it will let a supplier off the profile
  // page. Only formats were checked before, so a blank address saved happily,
  // reported "saved successfully", and then bounced the supplier straight back
  // here with nothing to indicate what was wrong.
  const REQUIRED = [
    ['supplierName', 'Company name'],
    ['registeredCountry', 'Registered country'],
    ['companyRegistrationNumber', 'Company registration number'],
    ['companyEmail', 'Company email'],
    ['physicalAddress', 'Physical address'],
  ];

  const handleSubmit = async () => {
    const blank = REQUIRED.filter(([key]) => !String(formData[key] || '').trim()).map(([, label]) => label);
    if (blank.length) {
      toast.error(`Please fill in: ${blank.join(', ')}. These are required before you can continue.`);
      return;
    }
    if (formData.companyRegistrationNumber && !isValidKenyaCompanyReg(formData.companyRegistrationNumber)) {
      toast.error('Company Registration Number format is invalid. ' + KENYA_REG_HELPER);
      return;
    }
    if (formData.companyEmail && !isValidEmail(formData.companyEmail)) {
      toast.error('Company email address format is invalid. ' + EMAIL_HELPER);
      return;
    }
    try {
      // Parse physical address - if it's a string, try to structure it
      // Otherwise, keep it as is if it's already structured
      let physicalAddressData = formData.physicalAddress;
      if (typeof physicalAddressData === 'string' && physicalAddressData.trim()) {
        // Try to parse the address string into structured format
        // For now, store as both physicalAddress (string) and companyPhysicalAddress (structured)
        const addressParts = physicalAddressData.split(',').map(part => part.trim());
        physicalAddressData = {
          street: addressParts[0] || '',
          city: addressParts[1] || '',
          country: addressParts[2] || formData.registeredCountry || '',
          postalCode: addressParts[3] || ''
        };
      }

      const updateData = {
        supplierName: formData.supplierName,
        entityType: formData.entityType !== loadedEntityType ? formData.entityType : undefined,
        registeredCountry: formData.registeredCountry,
        companyRegistrationNumber: formData.companyRegistrationNumber,
        companyEmail: formData.companyEmail,
        companyWebsite: formData.companyWebsite,
        physicalAddress: typeof formData.physicalAddress === 'string' ? formData.physicalAddress : undefined,
        companyPhysicalAddress: physicalAddressData,
        companyUpdateComment: formData.comment,
      };

      if (supplier?._id) {
        // Update existing supplier profile
        await api.put(`/suppliers/${supplier._id}`, updateData);
        toast.success('Company details saved successfully');

        // Check if profile is now complete — redirect to dashboard if so
        const isComplete = await checkSupplierProfileComplete(user);
        if (isComplete) {
          toast.success('Profile complete! Redirecting to dashboard...');
          navigate('/dashboard');
        } else {
          navigate('/profile');
        }
      } else {
        // Create supplier profile (not an application) - use PUT with upsert-like behavior
        // First create a minimal supplier record, then update it
        const registeredFullName = user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`.trim()
          : (user?.firstName || user?.lastName || 'Profile');

        const initialData = {
          supplierName: formData.supplierName || registeredFullName,
          legalNature: 'company', // Internal default; application captures actual entity type.
          serviceType: 'professional_services', // Default required field
          authorizedPerson: {
            name: registeredFullName,
            relationship: '',
            idPassportNumber: '',
            phone: '',
            email: user?.email || '',
          },
          status: 'draft', // Required, but this is just for profile storage
        };

        // Create the supplier record first with isProfileOnly flag
        const createResponse = await api.post('/suppliers/draft', {
          ...initialData,
          isProfileOnly: true, // Mark as profile-only, not an application
        });
        if (createResponse.data.success && createResponse.data.data?._id) {
          // Now update it with the full profile data
          await api.put(`/suppliers/${createResponse.data.data._id}`, {
            ...updateData,
            isProfileOnly: true, // Ensure it stays marked as profile-only
          });
          toast.success('Company details saved successfully');

          // Check if profile is now complete — redirect to dashboard if so
          const isComplete = await checkSupplierProfileComplete(user);
          if (isComplete) {
            toast.success('Profile complete! Redirecting to dashboard...');
            navigate('/dashboard');
          } else {
            navigate('/profile');
          }
        } else {
          throw new Error('Failed to create supplier profile');
        }
      }
    } catch (error) {
      console.error('Error submitting company details:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to submit update request';
      toast.error(errorMessage);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: '#ffffff', minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <Container maxWidth="lg" sx={{ flex: 1, py: { xs: 2, md: 4 }, px: { xs: 2, md: 3 } }}>
        {/* Back Button */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/profile')}
          sx={{
            color: '#6b7280',
            textTransform: 'none',
            fontSize: '14px',
            mb: 3,
            '&:hover': {
              backgroundColor: 'transparent',
              color: '#374151'
            }
          }}
        >
          Profile & Settings
        </Button>

        {/* Edit Company Details Form */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2, md: 3 },
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            backgroundColor: '#fff',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              mb: 0.5,
              fontSize: { xs: '16px', md: '18px' },
              color: '#111827'
            }}
          >
            Edit Company Details
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: '#6b7280',
              fontSize: { xs: '13px', md: '14px' },
              mb: 3
            }}
          >
            Changes to company details require approval
          </Typography>

          {/* Company Fields */}
          <Grid container spacing={{ xs: 2, md: 2.5 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '13px', md: '14px' }, color: '#374151' }}
              >
                Company name
              </Typography>
              <TextField
                fullWidth
                value={formData.supplierName}
                onChange={(e) => handleChange('supplierName', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '13px', md: '14px' }, color: '#374151' }}
              >
                Entity Type
              </Typography>
              <FormControl fullWidth size="small">
                <Select
                  value={formData.entityType}
                  onChange={(e) => handleChange('entityType', e.target.value)}
                  displayEmpty
                  disabled={entityTypeLocked}
                  sx={{ backgroundColor: entityTypeLocked ? '#f9fafb' : '#fff' }}
                >
                  <MenuItem value="" disabled>Select entity type</MenuItem>
                  {entityTypes.map((type) => (
                    <MenuItem key={type} value={type}>{type}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {entityTypeLocked && (
                <Typography sx={{ mt: 0.75, fontSize: '12px', color: '#6b7280' }}>
                  Your application has been submitted, so the entity type can no longer be changed here.
                  Contact procurement if it is wrong.
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '13px', md: '14px' }, color: '#374151' }}
              >
                Country of Incorporation
              </Typography>
              <ClickAwayListener onClickAway={() => setCountrySearchOpen(false)}>
                <Box>
                  <TextField
                    fullWidth
                    size="small"
                    value={formData.registeredCountry}
                    placeholder="Select country"
                    onClick={(e) => {
                      setCountryAnchorEl(e.currentTarget);
                      setCountrySearchOpen(!countrySearchOpen);
                      setCountrySearchTerm('');
                    }}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <KeyboardArrowDown sx={{ color: '#6b7280' }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        backgroundColor: '#fff',
                        cursor: 'pointer',
                      }
                    }}
                  />

                  {countrySearchOpen && (
                    <Paper
                      sx={{
                        mt: 0.5,
                        border: '1px solid #e5e7eb',
                        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        position: 'absolute',
                        zIndex: 1300,
                        width: countryAnchorEl ? countryAnchorEl.offsetWidth : 'auto',
                      }}
                    >
                      <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Search country..."
                          value={countrySearchTerm}
                          onChange={(e) => setCountrySearchTerm(e.target.value)}
                          autoFocus
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Search sx={{ color: '#9ca3af', fontSize: 20 }} />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              backgroundColor: 'transparent',
                              '& fieldset': {
                                border: 'none',
                              },
                              '&:hover fieldset': {
                                border: 'none',
                              },
                              '&.Mui-focused fieldset': {
                                border: 'none',
                              },
                              padding: 0,
                            }
                          }}
                        />
                      </Box>

                      <Box
                        sx={{
                          maxHeight: '280px',
                          overflowY: 'auto',
                          px: 1.5,
                          pb: 1,
                          '&::-webkit-scrollbar': {
                            width: '6px',
                          },
                          '&::-webkit-scrollbar-track': {
                            backgroundColor: '#f1f1f1',
                          },
                          '&::-webkit-scrollbar-thumb': {
                            backgroundColor: '#d1d5db',
                            borderRadius: '3px',
                          },
                        }}
                      >
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <Box
                              key={country.code}
                              onClick={() => {
                                handleChange('registeredCountry', country.name);
                                setCountrySearchOpen(false);
                                setCountrySearchTerm('');
                              }}
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                px: 1.5,
                                py: 1.25,
                                cursor: 'pointer',
                                borderRadius: '4px',
                                '&:hover': {
                                  backgroundColor: '#f9fafb'
                                }
                              }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography sx={{ fontSize: '20px' }}>{country.flag}</Typography>
                                <Typography sx={{ fontSize: '14px', color: '#374151' }}>
                                  {country.name}
                                </Typography>
                              </Box>
                              {formData.registeredCountry === country.name && (
                                <Check sx={{ color: '#578A18', fontSize: 20 }} />
                              )}
                            </Box>
                          ))
                        ) : (
                          <Box sx={{ p: 2, textAlign: 'center' }}>
                            <Typography sx={{ fontSize: '14px', color: '#9ca3af' }}>
                              No countries found
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Paper>
                  )}
                </Box>
              </ClickAwayListener>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '13px', md: '14px' }, color: '#374151' }}
              >
                Company Registration Number
              </Typography>
              <TextField
                fullWidth
                value={formData.companyRegistrationNumber}
                onChange={(e) => handleChange('companyRegistrationNumber', e.target.value)}
                size="small"
                error={!!formData.companyRegistrationNumber && !isValidKenyaCompanyReg(formData.companyRegistrationNumber)}
                helperText={
                  formData.companyRegistrationNumber && !isValidKenyaCompanyReg(formData.companyRegistrationNumber)
                    ? KENYA_REG_HELPER
                    : 'e.g. CPR/2022/123456, PVT/2021/123456, BN-123456'
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '13px', md: '14px' }, color: '#374151' }}
              >
                Company Email Address
              </Typography>
              <TextField
                fullWidth
                type="email"
                value={formData.companyEmail}
                onChange={(e) => handleChange('companyEmail', e.target.value)}
                size="small"
                error={!!formData.companyEmail && !isValidEmail(formData.companyEmail)}
                helperText={
                  formData.companyEmail && !isValidEmail(formData.companyEmail)
                    ? EMAIL_HELPER
                    : ''
                }
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '13px', md: '14px' }, color: '#374151' }}
              >
                Company Website
              </Typography>
              <TextField
                fullWidth
                value={formData.companyWebsite}
                onChange={(e) => handleChange('companyWebsite', e.target.value)}
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                  }
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                variant="body2"
                sx={{ mb: 1, fontWeight: 500, fontSize: { xs: '13px', md: '14px' }, color: '#374151' }}
              >
                Physical Address
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                value={formData.physicalAddress}
                onChange={(e) => handleChange('physicalAddress', e.target.value)}
                placeholder="Type your physical address here. Be as detailed as possible"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: '#fff',
                  }
                }}
              />
            </Grid>
          </Grid>

          {/* Comment Section */}
          <Box sx={{ mt: { xs: 3, md: 4 } }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                mb: 2,
                fontSize: { xs: '16px', md: '18px' }
              }}
            >
              Comment
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              value={formData.comment}
              onChange={(e) => handleChange('comment', e.target.value)}
              placeholder="Describe reasons for changes in company details"
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                }
              }}
            />
          </Box>

          {/* Action Buttons */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 2,
            mt: { xs: 3, md: 4 },
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
            <Button
              onClick={() => navigate('/profile')}
              sx={{
                textTransform: 'none',
                color: '#6b7280',
                borderColor: '#d1d5db',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  bgcolor: '#f9fafb',
                  borderColor: '#9ca3af',
                },
              }}
              variant="outlined"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              sx={{
                bgcolor: '#578A18',
                textTransform: 'none',
                width: { xs: '100%', sm: 'auto' },
                '&:hover': {
                  bgcolor: '#467014',
                },
              }}
            >
              Save
            </Button>
          </Box>
        </Paper>
      </Container>
      <Footer />
    </Box>
  );
};

export default EditCompanyDetails;
